/**
 * @file time-picker/time-picker.tsx
 *
 * Android MD3 clock picker. The picker uses a compact vertical arrangement in
 * portrait and moves the clock beside the display on larger landscape screens.
 */

import * as React from "react";
import { cn } from "../../../lib/utils";
import { ClockDial } from "./subcomponents/clock-dial";
import { PeriodSelector } from "./subcomponents/period-selector";
import { TimeField } from "./subcomponents/time-field";
import { TP_SIZE } from "./time-picker.tokens";
import type {
	TimePickerLayout,
	TimePickerProps,
	TimePickerState,
} from "./time-picker.types";

const getHourForDisplay = (state: TimePickerState): number => {
	const { hour, is24hour, isPm } = state;
	if (is24hour) return hour;
	if (hour % 12 === 0) return 12;
	return isPm ? hour - 12 : hour;
};

const getAutoLayout = (): Exclude<TimePickerLayout, "auto"> => {
	if (typeof window === "undefined") return "vertical";
	const { innerHeight, innerWidth } = window;
	return innerWidth >= 600 && innerHeight >= 300 && innerWidth >= innerHeight
		? "horizontal"
		: "vertical";
};

const useResolvedLayout = (
	layout: TimePickerLayout,
): Exclude<TimePickerLayout, "auto"> => {
	// Start vertical so SSR and the first client render agree; measure after mount.
	const [autoLayout, setAutoLayout] =
		React.useState<Exclude<TimePickerLayout, "auto">>("vertical");

	React.useEffect(() => {
		if (layout !== "auto") return;
		const updateLayout = () => setAutoLayout(getAutoLayout());
		updateLayout();
		window.addEventListener("resize", updateLayout);
		return () => window.removeEventListener("resize", updateLayout);
	}, [layout]);

	return layout === "auto" ? autoLayout : layout;
};

interface ClockDisplayProps {
	state: TimePickerState;
	layout: Exclude<TimePickerLayout, "auto">;
}

const ClockDisplay: React.FC<ClockDisplayProps> = ({ state, layout }) => {
	const { selection, setSelection, is24hour } = state;
	const periodLayout = layout === "horizontal" ? "horizontal" : "vertical";

	return (
		<div
			className={cn(
				"flex",
				layout === "horizontal"
					? "flex-col items-start justify-center"
					: "flex-row items-center justify-center",
			)}
		>
			<div className="flex items-center">
				<TimeField
					mode="selector"
					value={getHourForDisplay(state)}
					field="hour"
					selection={selection}
					onActivate={() => setSelection("hour")}
				/>
				<span
					aria-hidden
					className="w-6 text-center text-[57px] leading-none font-normal text-m3-on-surface"
				>
					:
				</span>
				<TimeField
					mode="selector"
					value={state.minute}
					field="minute"
					selection={selection}
					onActivate={() => setSelection("minute")}
				/>
			</div>
			{!is24hour ? (
				<PeriodSelector
					state={state}
					layout={periodLayout}
					className={layout === "horizontal" ? "mt-3" : "ml-3"}
				/>
			) : null}
		</div>
	);
};

export const TimePicker: React.FC<TimePickerProps> = ({
	state,
	layout = "auto",
	className,
}) => {
	const resolvedLayout = useResolvedLayout(layout);

	return (
		<div
			data-time-picker-layout={resolvedLayout}
			className={cn(
				"time-picker rounded-m3-xl bg-m3-surface-container-high p-6 select-none",
				className,
			)}
		>
			<p className="mb-5 text-left text-[12px] font-medium tracking-[0.1em] text-m3-on-surface-variant">
				Select time
			</p>
			<div
				className={cn(
					"flex",
					resolvedLayout === "horizontal"
						? "flex-row items-center gap-[52px]"
						: "flex-col items-center gap-9",
				)}
			>
				<ClockDisplay state={state} layout={resolvedLayout} />
				<ClockDial
					state={state}
					autoSwitchToMinute
					style={{
						width: TP_SIZE.clockDialSize,
						height: TP_SIZE.clockDialSize,
					}}
				/>
			</div>
		</div>
	);
};

TimePicker.displayName = "TimePicker";
